/**
 * Sysadmin Platform → Keys (Phase 5 PR-B v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.8 — cross-tenant view of JWT signing
 * keys (W1b-12), KMS data-at-rest keys (W1b-13), and TLS certificates
 * (W1b-13). Three flat tables stacked vertically, each preceded by a
 * "Generate / Rotate" action that opens a dialog.
 *
 * Destructive actions (force-retire JWT, revoke TLS) gate through
 * <TypedConfirmDialog> requiring the key's fingerprint or cert name
 * typed verbatim.
 *
 * Anti-pattern §11.5: tables are NOT wrapped in <Card>. Each section
 * has a small heading + action row, then the flat table.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Key, FileKey2, KeyRound, ShieldCheck, AlertTriangle, RefreshCw,
  Plus, Trash2, RotateCcw, Inbox, Clock,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import { StepUpReasonDialog } from '@/components/ui/step-up-reason-dialog';
import {
  useSysadminPlatformKeys, useRotateJwtKey, useForceRetireJwtKey,
  useRotateKmsKey, useRevokeTlsCert,
  type JwtKeyRow, type KmsKeyRow, type TlsCertRow,
  type JwtAlgorithm, type KmsAlgorithm,
} from '@/lib/use-sysadmin-platform';

export const Route = createFileRoute('/sysadmin/platform/keys')({
  component: KeysPage,
});

function KeysPage() {
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminPlatformKeys();

  // Dialog state.
  const [jwtRotateOpen, setJwtRotateOpen] = useState(false);
  const [kmsRotateOpen, setKmsRotateOpen] = useState(false);
  const [forceRetireTarget, setForceRetireTarget] = useState<JwtKeyRow | null>(null);
  const [tlsRevokeTarget, setTlsRevokeTarget] = useState<TlsCertRow | null>(null);

  const summary = data?.summary;

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load platform keys"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* ── Page heading ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Key className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Keys
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.8</Badge>
              <Badge variant="secondary" className="text-xs">PR-B</Badge>
              <span className="text-xs text-muted-foreground">
                JWT signing + KMS data-at-rest + TLS certs across every tenant
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={
              isFetching ? 'h-3.5 w-3.5 mr-1.5 animate-spin' : 'h-3.5 w-3.5 mr-1.5'
            }
          />
          Refresh
        </Button>
      </div>

      {/* ── 4-up summary ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="JWT — active"
          value={summary?.jwtActive ?? '—'}
          suffix={
            summary && summary.jwtDraining > 0
              ? `+ ${summary.jwtDraining} draining`
              : undefined
          }
          icon={KeyRound}
          tone="success"
        />
        <StatTile
          label="KMS — active"
          value={summary?.kmsActive ?? '—'}
          suffix={
            summary && summary.kmsReEncrypting > 0
              ? `+ ${summary.kmsReEncrypting} re-encrypting`
              : undefined
          }
          icon={ShieldCheck}
          tone="success"
        />
        <StatTile
          label="TLS — expiring < 30d"
          value={summary?.tlsExpiringSoon ?? '—'}
          icon={Clock}
          tone={
            summary && summary.tlsExpiringSoon > 0 ? 'warning' : 'success'
          }
        />
        <StatTile
          label="TLS — expired"
          value={summary?.tlsExpired ?? '—'}
          icon={AlertTriangle}
          tone={summary && summary.tlsExpired > 0 ? 'destructive' : 'muted'}
        />
      </div>

      {/* ── JWT signing keys ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              JWT signing keys
            </h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              7-day drain window on rotation. Force-retire bypasses the drain — typed-confirm required.
            </p>
          </div>
          <Button size="sm" onClick={() => setJwtRotateOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Generate new key
          </Button>
        </div>
        <JwtKeyTable
          keys={data?.jwtKeys ?? []}
          isLoading={isLoading}
          onForceRetire={setForceRetireTarget}
        />
      </section>

      {/* ── KMS keys ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              KMS keys
            </h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Data-at-rest encryption. Rotate inserts a new active and flips the prior to re-encrypting (lazy completion).
            </p>
          </div>
          <Button size="sm" onClick={() => setKmsRotateOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Rotate KMS key
          </Button>
        </div>
        <KmsKeyTable keys={data?.kmsKeys ?? []} isLoading={isLoading} />
      </section>

      {/* ── TLS certs ── */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <FileKey2 className="h-4 w-4 text-muted-foreground" />
            TLS certificates
          </h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Server certificates. Upload via the tenant Security tab; this view is cross-tenant. Soft-revoke flips status to revoked.
          </p>
        </div>
        <TlsCertTable
          certs={data?.tlsCertificates ?? []}
          isLoading={isLoading}
          onRevoke={setTlsRevokeTarget}
        />
      </section>

      {/* ── Dialogs ── */}
      {jwtRotateOpen && (
        <RotateJwtDialog
          open={jwtRotateOpen}
          onOpenChange={setJwtRotateOpen}
        />
      )}
      {kmsRotateOpen && (
        <RotateKmsDialog
          open={kmsRotateOpen}
          onOpenChange={setKmsRotateOpen}
        />
      )}
      {forceRetireTarget && (
        <ForceRetireJwtDialog
          target={forceRetireTarget}
          onClose={() => setForceRetireTarget(null)}
        />
      )}
      {tlsRevokeTarget && (
        <RevokeTlsDialog
          target={tlsRevokeTarget}
          onClose={() => setTlsRevokeTarget(null)}
        />
      )}
    </div>
  );
}

// ─── JWT table ───────────────────────────────────────────────────────────

function JwtKeyTable({
  keys,
  isLoading,
  onForceRetire,
}: {
  keys: JwtKeyRow[];
  isLoading: boolean;
  onForceRetire: (k: JwtKeyRow) => void;
}) {
  if (isLoading && keys.length === 0) {
    return (
      <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
        Loading JWT signing keys…
      </div>
    );
  }
  if (keys.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title="No JWT signing keys"
        body="Generate a key to start signing tokens for a tenant."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>KID</TableHead>
          <TableHead>Algorithm</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Fingerprint</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Retire at</TableHead>
          <TableHead className="w-24 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {keys.map((k) => (
          <TableRow key={k.id}>
            <TableCell className="text-xs">
              <div className="truncate max-w-[160px]" title={k.organizationName ?? k.organizationId}>
                {k.organizationName ?? <span className="text-muted-foreground">—</span>}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[160px]" title={k.organizationSlug ?? ''}>
                {k.organizationSlug ?? k.organizationId.slice(0, 8)}
              </div>
            </TableCell>
            <TableCell className="text-xs font-mono">{k.kid}</TableCell>
            <TableCell>
              <Badge variant="outline" className="text-[10px] font-mono">
                {k.algorithm}
              </Badge>
            </TableCell>
            <TableCell>
              {k.status === 'active' && (
                <StatusPill status="healthy" compact>Active</StatusPill>
              )}
              {k.status === 'draining' && (
                <StatusPill status="warning" compact>Draining</StatusPill>
              )}
              {k.status === 'retired' && (
                <StatusPill status="muted" compact>Retired</StatusPill>
              )}
            </TableCell>
            <TableCell className="text-[10px] font-mono text-muted-foreground">
              <div className="truncate max-w-[120px]" title={k.publicKeyFingerprint}>
                {k.publicKeyFingerprint.slice(0, 12)}…
              </div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
              {new Date(k.createdAt).toLocaleString()}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
              {k.retireAt ? new Date(k.retireAt).toLocaleString() : '—'}
            </TableCell>
            <TableCell className="text-right">
              {k.status !== 'retired' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onForceRetire(k)}
                  title="Force-retire this JWT key (bypasses 7-day drain)"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Retire
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── KMS table ───────────────────────────────────────────────────────────

function KmsKeyTable({
  keys,
  isLoading,
}: {
  keys: KmsKeyRow[];
  isLoading: boolean;
}) {
  if (isLoading && keys.length === 0) {
    return (
      <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
        Loading KMS keys…
      </div>
    );
  }
  if (keys.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title="No KMS keys"
        body="Rotate a key to start encrypting data-at-rest for a tenant."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>Alias</TableHead>
          <TableHead>Algorithm</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Rotation started</TableHead>
          <TableHead>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {keys.map((k) => (
          <TableRow key={k.id}>
            <TableCell className="text-xs">
              <div className="truncate max-w-[160px]" title={k.organizationName ?? k.organizationId}>
                {k.organizationName ?? <span className="text-muted-foreground">—</span>}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[160px]">
                {k.organizationSlug ?? k.organizationId.slice(0, 8)}
              </div>
            </TableCell>
            <TableCell className="text-xs font-mono">{k.alias}</TableCell>
            <TableCell>
              <Badge variant="outline" className="text-[10px] font-mono">
                {k.algorithm}
              </Badge>
            </TableCell>
            <TableCell>
              {k.status === 'active' && (
                <StatusPill status="healthy" compact>Active</StatusPill>
              )}
              {k.status === 're_encrypting' && (
                <StatusPill status="warning" compact>Re-encrypting</StatusPill>
              )}
              {k.status === 'retired' && (
                <StatusPill status="muted" compact>Retired</StatusPill>
              )}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
              {k.rotationStartedAt
                ? new Date(k.rotationStartedAt).toLocaleString()
                : '—'}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
              {new Date(k.createdAt).toLocaleString()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── TLS table ───────────────────────────────────────────────────────────

function TlsCertTable({
  certs,
  isLoading,
  onRevoke,
}: {
  certs: TlsCertRow[];
  isLoading: boolean;
  onRevoke: (c: TlsCertRow) => void;
}) {
  if (isLoading && certs.length === 0) {
    return (
      <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
        Loading TLS certificates…
      </div>
    );
  }
  if (certs.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title="No TLS certificates"
        body="Upload a cert via the tenant Security tab to appear in this list."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>SAN</TableHead>
          <TableHead>Issuer</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Expires in</TableHead>
          <TableHead>Auto-renew</TableHead>
          <TableHead className="w-24 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {certs.map((c) => (
          <TableRow key={c.id}>
            <TableCell className="text-xs">
              <div className="truncate max-w-[140px]" title={c.organizationName ?? c.organizationId}>
                {c.organizationName ?? <span className="text-muted-foreground">—</span>}
              </div>
            </TableCell>
            <TableCell className="text-xs font-medium">{c.name}</TableCell>
            <TableCell className="text-[10px] font-mono">
              <div className="truncate max-w-[200px]" title={c.san.join(', ')}>
                {c.san.slice(0, 2).join(', ')}
                {c.san.length > 2 && (
                  <span className="text-muted-foreground"> +{c.san.length - 2}</span>
                )}
              </div>
            </TableCell>
            <TableCell className="text-xs">
              <div className="truncate max-w-[160px]" title={c.issuer}>
                {c.issuer}
              </div>
            </TableCell>
            <TableCell>
              {c.status === 'active' && (
                <StatusPill status="healthy" compact>Active</StatusPill>
              )}
              {c.status === 'expired' && (
                <StatusPill status="destructive" compact>Expired</StatusPill>
              )}
              {c.status === 'revoked' && (
                <StatusPill status="muted" compact>Revoked</StatusPill>
              )}
            </TableCell>
            <TableCell className="text-xs tabular-nums">
              {c.daysToExpiry < 0 ? (
                <span className="text-destructive">{Math.abs(c.daysToExpiry)}d overdue</span>
              ) : c.daysToExpiry < 30 ? (
                <span className="text-amber-600">{c.daysToExpiry}d</span>
              ) : (
                <span className="text-muted-foreground">{c.daysToExpiry}d</span>
              )}
            </TableCell>
            <TableCell>
              {c.autoRenew ? (
                <Badge variant="outline" className="text-[10px]">
                  <RotateCcw className="h-2.5 w-2.5 mr-1" />
                  On
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">Off</Badge>
              )}
            </TableCell>
            <TableCell className="text-right">
              {c.status === 'active' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRevoke(c)}
                  title="Revoke this TLS certificate"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Revoke
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Generate JWT dialog ─────────────────────────────────────────────────

function RotateJwtDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [organizationId, setOrganizationId] = useState('');
  const [algorithm, setAlgorithm] = useState<JwtAlgorithm>('RS256');
  // #1131 — second stage prompts for the X-Step-Up-Reason audit text.
  const [reasonOpen, setReasonOpen] = useState(false);
  const rotate = useRotateJwtKey();

  // Basic UUID validation — the form is short enough to skip a heavy
  // validation library. The server enforces uuid via Zod anyway.
  const validUuid = useMemo(
    () =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        organizationId.trim(),
      ),
    [organizationId],
  );

  const handleProceedToReason = () => {
    if (!validUuid) return;
    setReasonOpen(true);
  };

  const handleRotate = (stepUpReason: string) => {
    if (!validUuid) return;
    rotate.mutate(
      {
        input: { organizationId: organizationId.trim(), algorithm },
        stepUpReason,
      },
      {
        onSuccess: () => {
          setReasonOpen(false);
          onOpenChange(false);
        },
        onError: () => setReasonOpen(false),
      },
    );
  };

  // #1131 — the second-stage reason prompt. Replaces the direct
  // rotate.mutate() call so the X-Step-Up-Reason header is always
  // populated.
  if (reasonOpen) {
    return (
      <StepUpReasonDialog
        open
        onOpenChange={(o) => !o && setReasonOpen(false)}
        title="Audit reason for JWT key rotation"
        description={`Tier-1 step-up. Inserts a new active key for tenant ${organizationId.slice(
          0,
          8,
        )}… (${algorithm}) and drains the prior active key over 7 days. The reason is recorded in the platform_audit_logs row.`}
        submitLabel={rotate.isPending ? 'Rotating…' : 'Rotate key'}
        submitting={rotate.isPending}
        onConfirm={handleRotate}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate JWT signing key</DialogTitle>
          <DialogDescription>
            Inserts a new active key for the target tenant and drains the prior active for 7 days.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Target tenant ID</label>
            <Input
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="h-9 text-sm font-mono"
            />
            {!validUuid && organizationId.length > 0 && (
              <p className="text-[10px] text-destructive">
                Enter a valid tenant UUID. See the Tenants rail to copy one.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Algorithm</label>
            <Select value={algorithm} onValueChange={(v) => setAlgorithm(v as JwtAlgorithm)}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RS256">RS256 — RSA 2048-bit (default)</SelectItem>
                <SelectItem value="ES256">ES256 — ECDSA P-256</SelectItem>
                <SelectItem value="EdDSA">EdDSA — Ed25519</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {rotate.error && (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {rotate.error.message}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleProceedToReason}
            disabled={!validUuid || rotate.isPending}
          >
            {rotate.isPending ? 'Generating…' : 'Continue to audit reason'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Generate KMS dialog ─────────────────────────────────────────────────

function RotateKmsDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [organizationId, setOrganizationId] = useState('');
  const [alias, setAlias] = useState('');
  const [algorithm, setAlgorithm] = useState<KmsAlgorithm>('AES256');
  // #1131 — second stage collects the X-Step-Up-Reason.
  const [reasonOpen, setReasonOpen] = useState(false);
  const rotate = useRotateKmsKey();

  const validUuid = useMemo(
    () =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        organizationId.trim(),
      ),
    [organizationId],
  );

  const canSubmit = validUuid && alias.trim().length > 0;

  const handleProceedToReason = () => {
    if (!canSubmit) return;
    setReasonOpen(true);
  };

  const handleRotate = (stepUpReason: string) => {
    if (!canSubmit) return;
    rotate.mutate(
      {
        input: {
          organizationId: organizationId.trim(),
          alias: alias.trim(),
          algorithm,
        },
        stepUpReason,
      },
      {
        onSuccess: () => {
          setReasonOpen(false);
          onOpenChange(false);
        },
        onError: () => setReasonOpen(false),
      },
    );
  };

  if (reasonOpen) {
    return (
      <StepUpReasonDialog
        open
        onOpenChange={(o) => !o && setReasonOpen(false)}
        title="Audit reason for KMS key rotation"
        description={`Tier-1 step-up. Rotates the ${alias.trim()} key for tenant ${organizationId.slice(
          0,
          8,
        )}… and starts a lazy re-encryption pass. The reason is recorded in the platform_audit_logs row.`}
        submitLabel={rotate.isPending ? 'Rotating…' : 'Rotate key'}
        submitting={rotate.isPending}
        onConfirm={handleRotate}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rotate KMS key</DialogTitle>
          <DialogDescription>
            Inserts a new active key for the (tenant, alias) pair and flips the prior to re-encrypting.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Target tenant ID</label>
            <Input
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="h-9 text-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Alias</label>
            <Input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="data-at-rest"
              className="h-9 text-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Algorithm</label>
            <Select value={algorithm} onValueChange={(v) => setAlgorithm(v as KmsAlgorithm)}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AES256">AES256 (default)</SelectItem>
                <SelectItem value="AES128">AES128</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {rotate.error && (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {rotate.error.message}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleProceedToReason}
            disabled={!canSubmit || rotate.isPending}
          >
            {rotate.isPending ? 'Rotating…' : 'Continue to audit reason'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Force-retire JWT typed-confirm ──────────────────────────────────────

function ForceRetireJwtDialog({
  target, onClose,
}: {
  target: JwtKeyRow;
  onClose: () => void;
}) {
  const retire = useForceRetireJwtKey();

  // #1131 — TypedConfirmDialog's requireReason field collects the
  // step-up audit reason. The mutation factory forwards it as the
  // X-Step-Up-Reason header.
  const handleConfirm = (
    payload: { reason?: string; secondApprover?: string } = {},
  ) => {
    retire.mutate(
      {
        input: { id: target.id, confirmed: true },
        stepUpReason: payload.reason?.trim() ?? '',
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <TypedConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Force-retire JWT signing key"
      description={`This bypasses the 7-day drain window and immediately invalidates ${target.kid} for tenant ${target.organizationSlug ?? target.organizationName ?? target.organizationId}. Any in-flight tokens signed by this key will fail validation on the next request.`}
      consequences={[
        `Key ${target.kid} flips to retired immediately`,
        'All tokens signed by this key fail validation',
        'A platform_audit_logs row records this with highRiskAction=true',
        'The 7-day rotation drain protection is bypassed',
      ]}
      confirmPhrase={target.kid}
      confirmLabel={retire.isPending ? 'Retiring…' : 'Force-retire key'}
      requireReason
      reasonPlaceholder="Audit reason: incident reference / ticket (3-500 chars)"
      onConfirm={handleConfirm}
    />
  );
}

// ─── Revoke TLS typed-confirm ────────────────────────────────────────────

function RevokeTlsDialog({
  target, onClose,
}: {
  target: TlsCertRow;
  onClose: () => void;
}) {
  const revoke = useRevokeTlsCert();

  // #1131 — X-Step-Up-Reason via TypedConfirmDialog.requireReason.
  const handleConfirm = (
    payload: { reason?: string; secondApprover?: string } = {},
  ) => {
    revoke.mutate(
      {
        input: { id: target.id, confirmed: true },
        stepUpReason: payload.reason?.trim() ?? '',
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <TypedConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Revoke TLS certificate"
      description={`This soft-revokes ${target.name} for tenant ${target.organizationSlug ?? target.organizationName ?? target.organizationId}. Services using this cert may interrupt traffic until a replacement is issued.`}
      consequences={[
        `Cert ${target.name} flips to revoked`,
        `Services in usedByServices may fail TLS handshakes`,
        'The row remains in the database for audit replay',
        'A platform_audit_logs row records this revoke',
      ]}
      confirmPhrase={target.name}
      confirmLabel={revoke.isPending ? 'Revoking…' : 'Revoke certificate'}
      requireReason
      reasonPlaceholder="Audit reason: incident reference / ticket (3-500 chars)"
      onConfirm={handleConfirm}
    />
  );
}
